import React, { useState, useCallback } from 'react';
import {
  ChatBubbleBottomCenterTextIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  SpeakerWaveIcon,
  ArrowPathIcon,
  LanguageIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { phrasebookAPI } from '../services/api';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';

// Map language names to BCP-47 codes for speechSynthesis
const LANG_CODES: Record<string, string> = {
  'Croatian': 'hr-HR', 'Spanish': 'es-ES', 'French': 'fr-FR', 'German': 'de-DE',
  'Italian': 'it-IT', 'Portuguese': 'pt-PT', 'Japanese': 'ja-JP', 'Korean': 'ko-KR',
  'Mandarin': 'zh-CN', 'Chinese': 'zh-CN', 'Cantonese': 'zh-HK', 'Thai': 'th-TH',
  'Vietnamese': 'vi-VN', 'Arabic': 'ar-SA', 'Turkish': 'tr-TR', 'Greek': 'el-GR',
  'Dutch': 'nl-NL', 'Polish': 'pl-PL', 'Czech': 'cs-CZ', 'Hungarian': 'hu-HU',
  'Romanian': 'ro-RO', 'Swedish': 'sv-SE', 'Norwegian': 'nb-NO', 'Danish': 'da-DK',
  'Finnish': 'fi-FI', 'Russian': 'ru-RU', 'Hindi': 'hi-IN', 'Indonesian': 'id-ID',
  'Malay': 'ms-MY', 'Slovenian': 'sl-SI', 'Serbian': 'sr-RS', 'Bulgarian': 'bg-BG',
  'Montenegrin': 'sr-ME', 'Bosnian': 'bs-BA', 'Albanian': 'sq-AL', 'Catalan': 'ca-ES',
  'Hebrew': 'he-IL', 'Swahili': 'sw-KE', 'Tagalog': 'tl-PH', 'Ukrainian': 'uk-UA',
};

function speak(text: string, language: string) {
  if (!window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  const langCode = LANG_CODES[language] || '';
  if (langCode) utterance.lang = langCode;
  utterance.rate = 0.85;
  utterance.pitch = 1;
  window.speechSynthesis.speak(utterance);
}

const PHRASE_CATEGORIES = [
  'Greetings & Basics', 'Dining & Food', 'Directions & Transport',
  'Shopping & Money', 'Emergencies & Help', 'Accommodation', 'Polite Expressions',
];

export const PhraseBook: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Language info
  const [destination, setDestination] = useState('');
  const [langInfo, setLangInfo] = useState<any>(null);
  const [selectedLanguage, setSelectedLanguage] = useState('');

  // Phrases
  const [phrasesResult, setPhrasesResult] = useState<any>(null);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState('');

  // Translate
  const [translateInput, setTranslateInput] = useState('');
  const [translateResult, setTranslateResult] = useState<any>(null);
  const [translateLoading, setTranslateLoading] = useState(false);

  const inputCls = "w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent dark:[color-scheme:dark]";
  const btnCls = "flex items-center gap-2 px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";
  const cardCls = "bg-white dark:bg-gray-800 rounded-lg p-5 shadow-sm border border-gray-200 dark:border-gray-700";

  const handleGetLanguageInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!destination.trim()) return;
    setLoading(true); setError(null); setPhrasesResult(null); setTranslateResult(null);
    try {
      const result = await phrasebookAPI.languageInfo({ destination: destination.trim() });
      setLangInfo(result);
      setSelectedLanguage(result.primary_language || '');
    } catch (err: any) { setError(err?.response?.data?.detail || 'Failed to get language info'); }
    finally { setLoading(false); }
  };

  const handleGetPhrases = async () => {
    if (!destination.trim()) return;
    setLoading(true); setError(null);
    try {
      const result = await phrasebookAPI.phrases({
        destination: destination.trim(),
        language: selectedLanguage || undefined,
        category: categoryFilter || undefined,
      });
      setPhrasesResult(result);
      if (result.categories?.length === 1) setExpandedCategory(result.categories[0].category);
    } catch (err: any) { setError(err?.response?.data?.detail || 'Failed to get phrases'); }
    finally { setLoading(false); }
  };

  const handleTranslate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!translateInput.trim() || !destination.trim()) return;
    setTranslateLoading(true); setError(null);
    try {
      const result = await phrasebookAPI.translate({
        destination: destination.trim(),
        language: selectedLanguage || undefined,
        phrase: translateInput.trim(),
      });
      setTranslateResult(result);
    } catch (err: any) { setError(err?.response?.data?.detail || 'Translation failed'); }
    finally { setTranslateLoading(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <LoadingOverlay isVisible={loading} message="Loading phrases..." subMessage="Preparing your phrase book" />

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <LanguageIcon className="h-7 w-7 text-cyan-600" />
          Phrase Book
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Learn essential phrases and translate on the go</p>
      </div>

      {/* Step 1: Select destination */}
      <form onSubmit={handleGetLanguageInfo} className={`${cardCls} mb-6`}>
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Destination</label>
            <input type="text" value={destination} onChange={e => setDestination(e.target.value)}
              placeholder="e.g., Tokyo, Dubrovnik, Barcelona" className={inputCls} />
          </div>
          <button type="submit" disabled={loading || !destination.trim()} className={btnCls}>
            <MagnifyingGlassIcon className="h-5 w-5" /> Go
          </button>
        </div>
      </form>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
          <span className="text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}

      {/* Language Info Panel */}
      {langInfo && (
        <div className={`${cardCls} mb-6`}>
          <div className="flex items-center gap-3 mb-3">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {langInfo.primary_language}
              {langInfo.primary_language_native && (
                <span className="text-cyan-600 dark:text-cyan-400 ml-2 text-base">{langInfo.primary_language_native}</span>
              )}
            </h2>
            {langInfo.tourist_english_level && (
              <span className="px-2 py-1 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 rounded-full text-xs">
                English: {langInfo.tourist_english_level}
              </span>
            )}
          </div>

          {/* Language selector if multiple */}
          {langInfo.additional_languages?.length > 0 && (
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Select language for phrases</label>
              <div className="flex flex-wrap gap-1.5">
                <button onClick={() => setSelectedLanguage(langInfo.primary_language)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    selectedLanguage === langInfo.primary_language ? 'bg-cyan-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}>{langInfo.primary_language}</button>
                {langInfo.additional_languages.map((lang: string) => (
                  <button key={lang} onClick={() => setSelectedLanguage(lang)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      selectedLanguage === lang ? 'bg-cyan-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}>{lang}</button>
                ))}
              </div>
            </div>
          )}

          {langInfo.language_tips && (
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1 mb-3">
              {langInfo.language_tips.map((tip: string, i: number) => (
                <li key={i} className="flex items-start gap-2">
                  <ChatBubbleBottomCenterTextIcon className="h-4 w-4 text-cyan-500 mt-0.5 flex-shrink-0" />
                  {tip}
                </li>
              ))}
            </ul>
          )}

          {/* Category filter + Load phrases */}
          <div className="flex gap-2 items-end mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Category (optional)</label>
              <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className={inputCls}>
                <option value="">All categories</option>
                {PHRASE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <button onClick={handleGetPhrases} disabled={loading} className={btnCls}>
              <SpeakerWaveIcon className="h-5 w-5" /> Load Phrases
            </button>
          </div>
        </div>
      )}

      {/* Phrases Results */}
      {phrasesResult?.categories && (
        <div className="space-y-3 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {phrasesResult.language} Phrases
          </h2>

          {phrasesResult.categories.map((cat: any, idx: number) => (
            <div key={idx} className={cardCls}>
              <button
                onClick={() => setExpandedCategory(expandedCategory === cat.category ? null : cat.category)}
                className="w-full flex items-center justify-between text-left"
              >
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">{cat.category}</h3>
                <span className="flex items-center gap-2 text-gray-400">
                  <span className="text-xs">{cat.phrases?.length || 0} phrases</span>
                  {expandedCategory === cat.category ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
                </span>
              </button>

              {expandedCategory === cat.category && cat.phrases && (
                <div className="mt-3 space-y-3">
                  {cat.phrases.map((phrase: any, pi: number) => (
                    <div key={pi} className="border-l-3 border-cyan-400 pl-3 py-1">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{phrase.english}</span>
                        <span className="text-sm text-gray-400">→</span>
                        <span className="text-sm font-semibold text-cyan-700 dark:text-cyan-300">{phrase.translated}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <button
                          onClick={() => speak(phrase.translated, phrasesResult.language)}
                          className="text-gray-400 hover:text-cyan-500 transition-colors flex-shrink-0"
                          title="Listen to pronunciation"
                        >
                          <SpeakerWaveIcon className="h-3.5 w-3.5" />
                        </button>
                        <span className="text-xs text-gray-500 dark:text-gray-400 italic">{phrase.pronunciation}</span>
                      </div>
                      {phrase.context && (
                        <p className="text-xs text-gray-400 mt-0.5">{phrase.context}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Translate Section */}
      {langInfo && (
        <div className={`${cardCls} mb-6`}>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <ArrowPathIcon className="h-5 w-5 text-cyan-600" />
            Translate a Phrase
          </h2>
          <form onSubmit={handleTranslate} className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                English → {selectedLanguage || langInfo.primary_language}
              </label>
              <input type="text" value={translateInput} onChange={e => setTranslateInput(e.target.value)}
                placeholder="Type a phrase to translate..." className={inputCls} />
            </div>
            <button type="submit" disabled={translateLoading || !translateInput.trim()} className={btnCls}>
              {translateLoading ? <ArrowPathIcon className="h-5 w-5 animate-spin" /> : <LanguageIcon className="h-5 w-5" />}
              Translate
            </button>
          </form>

          {translateResult && (
            <div className="mt-4 p-4 bg-cyan-50 dark:bg-cyan-900/10 rounded-lg border border-cyan-200 dark:border-cyan-800">
              <div className="flex items-baseline gap-2 flex-wrap mb-1">
                <span className="text-lg font-semibold text-cyan-800 dark:text-cyan-200">{translateResult.translated}</span>
                <span className="text-xs px-2 py-0.5 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 rounded-full">
                  {translateResult.formality || translateResult.language}
                </span>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <button
                  onClick={() => speak(translateResult.translated, translateResult.language || selectedLanguage || '')}
                  className="text-cyan-500 hover:text-cyan-400 transition-colors flex-shrink-0"
                  title="Listen to pronunciation"
                >
                  <SpeakerWaveIcon className="h-4 w-4" />
                </button>
                <span className="text-sm text-cyan-700 dark:text-cyan-300 italic">{translateResult.pronunciation}</span>
              </div>
              {translateResult.literal_meaning && (
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                  <span className="font-medium">Literal:</span> {translateResult.literal_meaning}
                </p>
              )}
              {translateResult.cultural_note && (
                <p className="text-xs text-gray-500 dark:text-gray-400 italic mt-1">{translateResult.cultural_note}</p>
              )}
              {translateResult.alternatives?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-cyan-200 dark:border-cyan-800 space-y-2">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Alternatives:</p>
                  {translateResult.alternatives.map((alt: any, i: number) => (
                    <div key={i} className="text-sm">
                      <span className="font-medium text-gray-800 dark:text-gray-200">{alt.translated}</span>
                      <span className="text-gray-400 mx-1">·</span>
                      <span className="italic text-gray-500 dark:text-gray-400">{alt.pronunciation}</span>
                      {alt.note && <span className="text-gray-400 text-xs ml-1">({alt.note})</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-8 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Translations are AI-generated and may not be perfect. For critical communication, consider using a professional translator or verified translation app.
        </p>
      </div>
    </div>
  );
};

export default PhraseBook;
